/**
 * Shared types, constants and pure helpers for the org chart (pages/Admin/OrgChart.tsx).
 * Nothing here talks to the API; it only shapes employee records for display.
 */
import type { Employee } from '../../../store/slices/employeeSlice';
import type { Department } from '../../../store/slices/departmentSlice';
import { sbg } from '../../../theme/sbgTokens';
import { AUTH_ROLE_LABELS } from '../../../utils/roles';
import { ACCESS_TAB_HREF, ACCESS_TAB_LABEL, ACCESS_TAB_LOCATION } from '../../../utils/settingsTabs';

export type EmployeeNode = Employee & { reports: EmployeeNode[] };
export type DensityMode = 'managers' | 'all';
export type OrgViewMode = 'tree' | 'teams' | 'list';
/** How a person was picked: keyboard picks move focus into the detail rail. */
export type SelectVia = 'pointer' | 'keyboard';

export interface VisualOrgNode {
  id: string;
  employee: EmployeeNode | null;
  children: VisualOrgNode[];
  groupedReports: EmployeeNode[];
  childCount: number;
  directCount: number;
  totalDescendants: number;
  isSynthetic?: boolean;
}

export interface PositionedOrgNode {
  id: string;
  data: VisualOrgNode;
  x: number;
  y: number;
}

export interface OrgLayoutLink {
  id: string;
  sourceId: string;
  targetId: string;
  path: string;
}

export interface OrgLayout {
  width: number;
  height: number;
  cardWidth: number;
  cardHeight: number;
  nodes: PositionedOrgNode[];
  links: OrgLayoutLink[];
  /** The top-level cards, left to right. */
  rootIds: string[];
}

export interface SalesManagerLane {
  id: string;
  manager: Employee;
  teamName: string;
  departmentId: string | null;
  departmentName: string;
  employees: Employee[];
  visibleEmployees: Employee[];
  directCount: number;
  linked: boolean;
  source: 'reports' | 'department' | 'both';
}

/** A department as the chart shows it: a fixed series color (or neutral) and a headcount. */
export interface DepartmentSwatch {
  key: string;
  label: string;
  color: string;
  count: number;
  /** True for the departments that hold one of the eight categorical series slots. */
  colored: boolean;
  /** True for the "No department" bucket. */
  isNone: boolean;
}

/* Card geometry (the d3 tree spaces nodes from these). */
export const MANAGER_CARD_WIDTH = 248;
export const MANAGER_CARD_HEIGHT = 128;
export const PERSON_CARD_WIDTH = 224;
export const PERSON_CARD_HEIGHT = 88;
export const MAP_PADDING = 40;

export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 1.5;
export const ZOOM_STEP = 0.15;
/** The chart opens at full size so card text keeps its real size (12px and up). */
export const INITIAL_ZOOM = 1;
/**
 * Below this zoom the cards switch to an overview: name and headcount in
 * larger type, no avatars or department line, so a fitted org stays legible.
 */
export const OVERVIEW_ZOOM = 0.75;
/** Rows a Sales lane shows before "Show all". */
export const LANE_PREVIEW = 8;
/** The detail rail sits beside the chart only when the page is at least this wide. */
export const RAIL_MIN_PAGE_WIDTH = 1100;

export const UNASSIGNED_TEAM = 'Unassigned';
export const ALL_DEPARTMENTS = '__all__';
export const NO_DEPARTMENT_KEY = '__none__';
export const NO_DEPARTMENT_LABEL = 'No department';
/** Neutral swatch for departments past the eighth series slot and for people without one. */
export const NEUTRAL_SWATCH = sbg.border.strong;

export const sortByName = (a: Employee, b: Employee) => a.name.localeCompare(b.name);

export const getEmployeeTeamLabel = (employee: Employee): string =>
  employee.teamName || employee.department?.name || UNASSIGNED_TEAM;

const normalizeWord = (value: string) => value.trim().toLowerCase().replace(/[\s._-]+/g, '');

/** Access-role words (admin, HR, manager, Jr manager, employee) in any casing or spacing. */
const SYSTEM_ROLE_WORDS = new Set<string>(
  [...Object.keys(AUTH_ROLE_LABELS), ...Object.values(AUTH_ROLE_LABELS)].map(normalizeWord),
);

/**
 * The person's job title, or null when there isn't a real one on file.
 * `employees.role` often holds an access role ("employee", "Manager") or just
 * repeats the department or team; none of those are titles.
 */
export const jobTitleOf = (employee: Employee): string | null => {
  const role = employee.role?.trim();
  if (!role) return null;
  const word = normalizeWord(role);
  if (SYSTEM_ROLE_WORDS.has(word)) return null;
  if (employee.department?.name && normalizeWord(employee.department.name) === word) return null;
  if (employee.teamName && normalizeWord(employee.teamName) === word) return null;
  return role;
};

/** The line under a person's name: their job title, else their team. */
export const titleLineOf = (employee: Employee): string => jobTitleOf(employee) ?? getEmployeeTeamLabel(employee);

export const employeeMatchesSearch = (employee: Employee, term: string): boolean => {
  if (!term.trim()) return true;
  const lower = term.trim().toLowerCase();
  return (
    employee.name.toLowerCase().includes(lower) ||
    employee.email.toLowerCase().includes(lower) ||
    (employee.teamName || '').toLowerCase().includes(lower) ||
    (employee.department?.name || '').toLowerCase().includes(lower)
  );
};

export const isSalesEmployee = (employee: Employee, salesDepartmentNames: Set<string>): boolean => {
  const departmentName = employee.department?.name;
  return (
    Boolean(employee.department?.isSalesTeam)
    || Boolean(departmentName && salesDepartmentNames.has(departmentName))
  );
};

export const departmentKeyOf = (employee: Employee): string => {
  const department = employee.department;
  if (!department) return NO_DEPARTMENT_KEY;
  return department._id || department.id || (department.name ? `name:${department.name}` : NO_DEPARTMENT_KEY);
};

const departmentRank = (departments: Department[]) => {
  const ordered = [...departments].sort(
    (a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0) || a.name.localeCompare(b.name),
  );
  const rank = new Map<string, number>();
  ordered.forEach((department, index) => {
    if (department._id) rank.set(department._id, index);
    if (department.id) rank.set(department.id, index);
    rank.set(`name:${department.name}`, index);
  });
  return rank;
};

/**
 * Departments in a fixed order by entity (book §13.3): oldest department first
 * (then by name), so a department keeps its color when people move or
 * headcounts change. The first eight with people wear `sbg.chart.series`; the
 * rest and "No department" wear a neutral swatch (a ninth series folds into "Other").
 */
export const buildDepartmentSwatches = (employees: Employee[], departments: Department[] = []): DepartmentSwatch[] => {
  const byKey = new Map<string, { label: string; count: number }>();
  employees.forEach((employee) => {
    const key = departmentKeyOf(employee);
    const label = key === NO_DEPARTMENT_KEY ? NO_DEPARTMENT_LABEL : employee.department?.name || NO_DEPARTMENT_LABEL;
    const entry = byKey.get(key);
    if (entry) entry.count += 1;
    else byKey.set(key, { label, count: 1 });
  });

  const rank = departmentRank(departments);
  const rankOf = (key: string, label: string) => rank.get(key) ?? rank.get(`name:${label}`) ?? Number.MAX_SAFE_INTEGER;
  const real = Array.from(byKey.entries())
    .filter(([key]) => key !== NO_DEPARTMENT_KEY)
    .sort((a, b) => rankOf(a[0], a[1].label) - rankOf(b[0], b[1].label) || a[1].label.localeCompare(b[1].label));

  const swatches: DepartmentSwatch[] = real.map(([key, entry], index) => ({
    key,
    label: entry.label,
    count: entry.count,
    colored: index < sbg.chart.series.length,
    color: index < sbg.chart.series.length ? sbg.chart.series[index] : NEUTRAL_SWATCH,
    isNone: false,
  }));

  const none = byKey.get(NO_DEPARTMENT_KEY);
  if (none) {
    swatches.push({ key: NO_DEPARTMENT_KEY, label: NO_DEPARTMENT_LABEL, count: none.count, colored: false, color: NEUTRAL_SWATCH, isNone: true });
  }
  return swatches;
};

/** The series slot (1-based) for a department, or null when it wears the neutral swatch. */
export const seriesSlotOf = (swatches: DepartmentSwatch[], key: string): number | null => {
  const index = swatches.findIndex((swatch) => swatch.key === key);
  return index >= 0 && swatches[index].colored ? index + 1 : null;
};

/**
 * An org-chart connector: down from the parent, across, and down into the
 * child, with rounded elbows (book: 1px border.strong, rounded corners).
 */
export const buildElbowPath = (sourceX: number, sourceY: number, targetX: number, targetY: number, radius = 10) => {
  const midY = sourceY + Math.max(20, (targetY - sourceY) / 2);
  const dx = targetX - sourceX;
  if (Math.abs(dx) < 1) return `M ${sourceX} ${sourceY} V ${targetY}`;
  const r = Math.max(0, Math.min(radius, Math.abs(dx) / 2, midY - sourceY, targetY - midY));
  const dir = dx > 0 ? 1 : -1;
  return [
    `M ${sourceX} ${sourceY}`,
    `V ${midY - r}`,
    `Q ${sourceX} ${midY} ${sourceX + dir * r} ${midY}`,
    `H ${targetX - dir * r}`,
    `Q ${targetX} ${midY} ${targetX} ${midY + r}`,
    `V ${targetY}`,
  ].join(' ');
};

export const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

export const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Decelerating curve close to --sbg-ease-enter (cubic-bezier(0.16, 1, 0.3, 1)). */
export const easeEnter = (t: number) => 1 - Math.pow(1 - t, 4);

const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

/** A failed save, in words: what happened, why (when the server says), and what to do next. */
export interface SaveErrorCopy {
  message: string;
  /** Set when the fix lives on another screen. */
  action?: { label: string; to: string };
}

/**
 * Turns an RTK Query error from a placement save into brand error copy.
 * `what` is the sentence that names the failed change ("Couldn't move Brett Green under Thomas Flynn.").
 * `manager` is the person they were moving under, when there is one.
 */
export const describeSaveError = (
  error: unknown,
  what: string,
  options: { manager?: { name: string } | null; keepsForm?: boolean } = {},
): SaveErrorCopy => {
  const { status, data } = (error ?? {}) as { status?: unknown; data?: unknown };
  const serverMessage = (() => {
    if (!data || typeof data !== 'object') return '';
    const body = data as { error?: unknown; message?: unknown };
    const text = typeof body.error === 'string' ? body.error : typeof body.message === 'string' ? body.message : '';
    return text.trim();
  })();
  const formNote = options.keepsForm ? ' Your changes are still in the form.' : '';

  if (typeof status !== 'number' || status >= 500) {
    return { message: `${what} The server didn't respond. Try again in a minute.${formNote}` };
  }
  if (status === 400 && /manager/i.test(serverMessage) && options.manager) {
    const first = firstNameOf(options.manager.name);
    return {
      message: `${what} ${options.manager.name}'s account can't manage people yet. Give ${first} the Manager role in ${ACCESS_TAB_LOCATION}, then try again.`,
      action: { label: `Open ${ACCESS_TAB_LABEL}`, to: ACCESS_TAB_HREF },
    };
  }
  if (status === 400 && /department not found/i.test(serverMessage)) {
    return { message: `${what} That team's department no longer exists. Reload the page to get the current departments, then try again.` };
  }
  if (status === 401) {
    return { message: `${what} Your session ended. Sign in again, then retry the change.` };
  }
  if (status === 403) {
    return { message: `${what} Your account can't change reporting lines. Ask an admin or HR to make this change.` };
  }
  if (status === 404) {
    return { message: `${what} This person's record no longer exists. Reload the page to see the current org chart.` };
  }
  const reason = serverMessage ? ` The server said: ${serverMessage.replace(/\.?$/, '.')}` : '';
  return { message: `${what}${reason} Check the placement and try again.${formNote}` };
};
